import React, {
  Children,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';

import { WithTranslation } from 'react-i18next';
import { LegacyHistory } from 'ia-react-core/~/interfaces/LegacyHistory';
import WorkflowContext from '../_WorkflowContext';
import cancelWorkflowStepEntryById from '../../../actions/workflow-fn/cancelWorkflowStepEntryById';
import confirmWorkflowStepEntryById from '../../../actions/workflow-fn/confirmWorkflowStepEntryById';
import useScopedSelector from '../../../hooks/useScopedSelector';
import ConfirmModal from '../../modal/ConfirmModal';
import { StepProps } from '../Step';

export interface WorkflowProps {
  /** A series of [Step](/Core/Components/Workflow/Step). */
  children?: ReactNode;
  /** The name of the workflow, used as the reducer key. If not provided, the reducer key will be "Workflow${workflowId}". */
  name?: string;
  /** The path on the redux store where to write the values to, based on the current scope. */
  valuesDst?: string;
  /** Clean the values from the state when unmounting the workflow. */
  cleanValues?: boolean;
  /** Content of the confirmation title. */
  confirmationTitle?: ReactNode;
  /** Content of the confirmation modal. */
  confirmationContent?: ReactNode;
  /** Custom label for the cancel button of the confirmation modal. */
  cancelButton?: ReactNode;
  /** Custom label for the confirm button of the confirmation modal. */
  confirmButton?: ReactNode;
  /** Set the initial step name. */
  initialStepName?: string;
  /** @ignore */
  workflowId?: number;
}

interface WorkflowInternalProps extends WithTranslation {
  workflowId: number;
  name: string;
  scope: string[];
  valuesSrc?: string;
  history?: LegacyHistory;
  basename?: string;
}

export interface WorkflowComposedProps extends Omit<WorkflowProps, 'workflowId' | 'name'>, WorkflowInternalProps {}

const WorkflowDefaultProps = {
  cleanValues: false,
};

const Workflow: FC<WorkflowComposedProps> = ({
  t, children, name, workflowId, valuesDst, scope, confirmationTitle, confirmationContent, cancelButton, confirmButton,
}) => {
  const confirmModalName = useMemo(() => `workflow${workflowId}ConfirmModal`, [workflowId]);
  const dispatch = useDispatch();

  const dispatchCancelWorkflowStepEntryById = useCallback(() => (
    dispatch(cancelWorkflowStepEntryById(workflowId))
  ), [dispatch, workflowId]);

  const dispatchConfirmWorkflowStepEntryById = useCallback(() => (
    dispatch(confirmWorkflowStepEntryById(workflowId))
  ), [dispatch, workflowId]);

  const currentStepName: string = useScopedSelector(`${name}.currentStepName`);

  const workflowContext = useMemo(() => ({
    name,
    workflowId,
    valuesDst,
    workflowReducerScope: scope,
    currentStepName,
  }), [name, workflowId, valuesDst, scope, currentStepName]);

  const childrenArray = useMemo(() => Children.toArray(children) as ReactElement<StepProps>[], [children]);
  const childrenMap = useMemo(() => childrenArray.reduce<Record<string, ReactElement<{ children: ReactElement}>>>((map, step) => {
    map[step.props.name] = step as ReactElement;
    return map;
  }, {}), [childrenArray]);

  const content = useMemo(() => {
    const step = childrenMap[currentStepName]?.props?.children as ReactNode | (() => ReactNode);
    if (typeof step === 'function') return step();
    if (step) return step;
    return null;
  }, [childrenMap, currentStepName]);

  const handleConfirm = useCallback(() => {
    dispatchConfirmWorkflowStepEntryById();
  }, [dispatchConfirmWorkflowStepEntryById]);

  return (
    <>
      <ConfirmModal
        name={confirmModalName}
        modalTitle={confirmationTitle || t('confirmationTitle')}
        cancelButton={cancelButton}
        confirmButton={confirmButton}
        onCancel={dispatchCancelWorkflowStepEntryById}
        onClose={dispatchCancelWorkflowStepEntryById}
        onConfirm={handleConfirm}
      >
        {confirmationContent || t('confirmationContent')}
      </ConfirmModal>
      <WorkflowContext.Provider value={workflowContext}>
        {content}
      </WorkflowContext.Provider>
    </>
  );
};

Workflow.defaultProps = WorkflowDefaultProps;

export default Workflow;
